import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useSnackbar } from 'notistack';
import useAuth from 'hooks/useAuth';
import Layout from '../../../layouts';
import Page from '../../../components/Page';
import axios from '../../../utils/axios';
import { PATH_DASHBOARD } from '../../../routes/paths';
import { isMaster, podeGravarCadastroAdmin } from '../../../utils/perfil';
import { BotaoExportar, CartaoIndicador, FiltroSegmentado, Num, PillStatus, CABECALHO_TABELA } from '../../../components/gps';
import { Skeleton } from '../../../components/ui/skeleton';
import ModalNovoAplicativo from '../../../components/gps/ModalNovoAplicativo';
import ModalAplicativo from '../../../components/gps/ModalAplicativo';
import MenuAcoes, { ResumoExclusao } from '../../../components/gps/MenuAcoes';
import Modal, { BotaoSecundario } from '../../../components/gps/Modal';

import ModalDetalheApp from '../../../components/gps/ModalDetalheApp';
import IconeAplicativo from '../../../components/gps/IconeAplicativo';
import ModalLimparCache from '../../../components/gps/ModalLimparCache';
import { linhasIniciais, OPCOES_LINHAS, rotuloLinhas } from '../../../components/gps/paginacao';
import TituloTela from '../../../components/gps/TituloTela';
import CaixaBusca from '../../../components/gps/CaixaBusca';

/* ============================================================================
   Aplicativos - PADRAO VISUAL NOVO (shadcn + Tailwind)

   Rota PARALELA (/dashboard/aplicativos-novo) de proposito. A tela antiga em
   /dashboard/aplicativos continua intacta e funcionando: assim da para comparar
   as duas lado a lado com dados reais antes de trocar, e um problema aqui nao
   impede ninguem de trabalhar. Quando aprovada, esta substitui a antiga e a rota
   volta a ser /dashboard/aplicativos.

   SOMENTE LEITURA: esta tela nao grava nada. As acoes de escrita (novo
   aplicativo, ativar/inativar em lote) entram depois, junto com o modal.

   Contrato reaproveitado da tela atual, sem inventar endpoint:
     GET /aplicativos/paginadoDispositivosEGrupos
         ?page&rows&valor&campo&direcao&ordenar&status
     -> { content: [...], totalElements: n }
   ============================================================================ */

// Campos conferidos em remoteAPI/src/app/aplicativos/aplicativos.service.ts
// (paginadoDispositivosEGrupos): a API devolve `name` (nao `nome`),
// `gruposCount`/`dispositivosCount` e o status como STRING do enum StatusCore
// ("ATIVO"/"INATIVO"), nao como numero.
type Aplicativo = {
  name?: string;
  packageName?: string;
  status?: number | string;
  gruposCount?: number;
  dispositivosCount?: number;
};


// O backend usa status 1 = ativo, 0 = inativo e -1 = todos.
const STATUS = { TODOS: -1, ATIVO: 1, INATIVO: 0 } as const;

// Grade unica para cabecalho e linha: e o que garante que as colunas fiquem
// alinhadas sem <table>, e permite a coluna numerica virar regua vertical.
const GRADE = '32px minmax(200px,2.4fr) minmax(180px,2fr) 96px 120px 116px 40px';

// A API converte o status numerico em nome de enum antes de responder
// (`StatusCore[item.status]`), entao chega "ATIVO"/"INATIVO". Aceitamos tambem o
// numero porque outros endpoints do projeto devolvem 1/0 - mesma tolerancia que a
// tela antiga tem em InvoiceTableRow.tsx.
function ehAtivo(a: Aplicativo) {
  const s = a?.status;
  return String(s).toUpperCase() === 'ATIVO' || Number(s) === 1;
}

function numero(valor: unknown) {
  const n = Number(valor);
  return Number.isFinite(n) ? n : 0;
}

Aplicativos.getLayout = function getLayout(page: React.ReactElement) {
  return <Layout>{page}</Layout>;
};

export default function Aplicativos() {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [menuAberto, setMenuAberto] = useState<string | null>(null);
  const [apagando, setApagando] = useState<Aplicativo | null>(null);
  const [removendo, setRemovendo] = useState(false);
  const [cacheDe, setCacheDe] = useState<Aplicativo | null>(null);
  // Pacote em edicao no modal de cartoes. `undefined` = fechado.
  const [editando, setEditando] = useState<string | undefined>(undefined);
  const [detalhe, setDetalhe] = useState<{ app: Aplicativo; modo: 'grupos' | 'dispositivos' } | null>(null);
  const [linhas, setLinhas] = useState<Aplicativo[]>([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(0);

  /* QUANTAS LINHAS POR PAGINA - ESCOLHA DA PESSOA (10/09/2026, a pedido).

     Era uma constante de modulo (`linhasIniciais()`), calculada UMA vez na
     carga do arquivo: quem abrisse em 1366x768 ficava presa em 5 linhas para
     sempre, mesmo maximizando a janela depois. `linhasIniciais()` continua
     dando o valor de PARTIDA - a altura da tela e um bom palpite inicial -,
     mas agora e so o palpite. */
  const [linhasPorPagina, setLinhasPorPagina] = useState(linhasIniciais());
  /* `busca` e o termo que vai para a API. So muda no Enter ou na lupa da
     CaixaBusca (21/10/2026): antes mudava 350 ms depois de cada pausa na
     digitacao, uma consulta por pausa. */
  const [busca, setBusca] = useState('');
  const pedidoAtual = useRef(0);
  /* ATIVOS por padrao, e nao "Todos" - pedido de 25/08/2026.

     O trabalho do dia a dia acontece sobre o que esta ativo; os inativos sao
     historico. Abrindo em "Todos", a primeira pagina vinha misturada e era preciso
     filtrar toda vez para chegar ao que interessa.

     Os CONTADORES dos tres segmentos continuam vindo completos (vem de endpoint
     proprio de indices), entao o total geral segue visivel e "Todos" e um clique. */
  const [status, setStatus] = useState<number>(STATUS.ATIVO);
  const [modalAberto, setModalAberto] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  // Contadores vindos de /aplicativos/findByStatus, o mesmo que a tela antiga usa
  // para os cartoes - evita uma segunda fonte de verdade para os mesmos numeros.
  const [indices, setIndices] = useState<{ total: number; ativos: number; inativos: number }>({
    total: 0,
    ativos: 0,
    inativos: 0,
  });

  const carregar = useCallback(async () => {
    const meuPedido = ++pedidoAtual.current;
    setCarregando(true);
    setErro('');
    try {
      // ATENCAO: `campo` e `ordenar` sao interpolados em SQL BRUTO no backend
      // (`aplicativo.${campo}`), entao aqui so podem sair as duas constantes
      // abaixo - NUNCA texto do usuario. A coluna e NAME, nao NOME: mandar
      // "nome" gerava "ORDER BY aplicativo.nome", coluna inexistente, e a
      // listagem respondia 500 em toda carga.
      //
      // A busca unica "Nome ou pacote" resolve o campo pelo formato do que foi
      // digitado: pacote tem ponto (com.exemplo.app), nome nao. Assim uma caixa
      // so cobre os dois casos, sem o seletor de campo que a tela antiga exigia.
      const campo = busca.includes('.') ? 'packageName' : 'name';

      const resposta = await axios.get<{ content: Aplicativo[]; totalElements: number }>(
        `/aplicativos/paginadoDispositivosEGrupos?page=${pagina}&rows=${linhasPorPagina}` +
          `&valor=${encodeURIComponent(busca)}&campo=${campo}&direcao=asc&ordenar=name&status=${status}`
      );
      if (meuPedido !== pedidoAtual.current) return; // resposta de busca antiga
      setLinhas(resposta.data?.content || []);
      setTotal(Number(resposta.data?.totalElements) || 0);
    } catch (e: any) {
      if (meuPedido !== pedidoAtual.current) return;
      // Erro separado de "vazio": lista vazia e informacao, falha de rede e outra
      // coisa - misturar os dois faz o usuario achar que nao existe dado.
      setErro(e?.response?.data?.message || 'Não foi possível carregar os aplicativos.');
      setLinhas([]);
      setTotal(0);
    } finally {
      if (meuPedido === pedidoAtual.current) setCarregando(false);
    }
  }, [pagina, linhasPorPagina, busca, status]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    axios
      .get<any>('/aplicativos/findByStatus')
      .then((r) => {
        // findByStatus responde { total, ativo, inativo } - singular, conferido
        // no service. Antes eu tentava `ativos`/`inativos` primeiro, nomes que a
        // API nunca devolve: funcionava por acidente, pelo fallback.
        const d = r.data || {};
        setIndices({
          total: numero(d.total),
          ativos: numero(d.ativo),
          inativos: numero(d.inativo),
        });
      })
      .catch(() => setIndices({ total: 0, ativos: 0, inativos: 0 }));
  }, []);

  const apagar = async () => {
    if (!apagando?.packageName) return;
    setRemovendo(true);
    try {
      // A tela antiga apaga por packageName (aplicativos/index.tsx:425), nao por
      // um id numerico - o endpoint e DELETE /aplicativos/:name.
      await axios.delete(`/aplicativos/${encodeURIComponent(apagando.packageName)}`);
      enqueueSnackbar('Aplicativo apagado.', { variant: 'success' });
      setApagando(null);
      await carregar();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Não foi possível apagar o aplicativo.', {
        variant: 'error',
      });
    } finally {
      setRemovendo(false);
    }
  };

  const totalPaginas = Math.max(1, Math.ceil(total / linhasPorPagina));
  const baseIndices = indices.total || total;

  const opcoesStatus = useMemo(
    () => [
      { valor: STATUS.TODOS as number, rotulo: 'Todos', total: indices.total || total },
      { valor: STATUS.ATIVO as number, rotulo: 'Ativos', total: indices.ativos },
      { valor: STATUS.INATIVO as number, rotulo: 'Inativos', total: indices.inativos },
    ],
    [indices, total]
  );

  return (
    <Page title="Aplicativos">
      <div className="mx-auto w-full max-w-[var(--gps-pagina-max)] px-4 pb-2 pt-2 font-sans text-ink">
        <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
          <div>
            <TituloTela
              descricao={
                <>
<Num>{(indices.total || total).toLocaleString('pt-BR')}</Num> aplicativos catalogados no parque.
              Filtre por status ou pacote para agir em lote.
                </>
              }
            >
              Aplicativos
            </TituloTela>
          </div>

          {/* Acao primaria a DIREITA do cabecalho, como no mockup, com o Excel ao
              lado. Exporta a PAGINA ATUAL - e o que esta na tela, e o rotulo do
              rodape diz quantos sao; prometer o total exigiria buscar 791 linhas
              de novo so para gerar arquivo. */}
          <div className="flex flex-none items-center gap-2">
            <button
              type="button"
              onClick={() => setModalAberto(true)}
              className="h-9 flex-none rounded-lg bg-primary px-4 text-[12.5px] font-bold text-primary-foreground"
            >
              + Novo aplicativo
            </button>
          </div>
        </div>

        {/* Indicadores. Cada um traz a comparacao junto do numero - a proporcao
            sobre a base e o que transforma "14" em "14 de 791". */}
        <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <CartaoIndicador
            rotulo="Total"
            valor={(indices.total || total).toLocaleString('pt-BR')}
            legenda="catalogados"
            tom="info"
            proporcao={1}
          />
          <CartaoIndicador
            rotulo="Ativos"
            valor={indices.ativos.toLocaleString('pt-BR')}
            legenda="chegando aos dispositivos"
            delta={baseIndices ? `${Math.round((indices.ativos / baseIndices) * 100)}% da base` : undefined}
            tom="ok"
            proporcao={baseIndices ? indices.ativos / baseIndices : 0}
          />
          <CartaoIndicador
            rotulo="Inativos"
            valor={indices.inativos.toLocaleString('pt-BR')}
            legenda="revisar"
            delta={baseIndices ? `${Math.round((indices.inativos / baseIndices) * 100)}% da base` : undefined}
            tom="danger"
            proporcao={baseIndices ? indices.inativos / baseIndices : 0}
          />
        </div>

        {/* Toolbar: busca unica (nome ou pacote) + segmentado com contagem.
            Substitui os selects soltos - a contagem aparece sem abrir nada. */}
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <CaixaBusca
            valor={busca}
            aoBuscar={(v) => {
              setPagina(0);
              setBusca(v.trim());
            }}
            placeholder="Nome ou pacote"
            className="w-full max-w-[320px]"
          />
          <FiltroSegmentado
            opcoes={opcoesStatus}
            valor={status}
            onChange={(v) => {
              setPagina(0);
              setStatus(v);
            }}
          />

          {/* EXCEL DESCEU do cabecalho para a barra de filtros (27/08/2026, a
              pedido: em HD ele disputava a linha do titulo).

              O '+ Novo aplicativo' FICOU no cabecalho - ele e a acao primaria da
              tela, e acao primaria mora junto do titulo. O Excel age sobre a
              LISTA (exporta o que esta na tela), entao pertence a barra que
              define a lista. `ml-auto` separa ele dos filtros, a direita. */}
          <span className="ml-auto flex flex-none items-center">
              <BotaoExportar
                nomeArquivo="aplicativos"
                linhas={() =>
                  linhas.map((a) => ({
                    Nome: a.name ?? '',
                    Pacote: a.packageName ?? '',
                    Grupos: numero(a.gruposCount),
                    Dispositivos: numero(a.dispositivosCount),
                    Status: ehAtivo(a) ? 'ATIVO' : 'INATIVO',
                  }))
                }
              />
          </span>
        </div>

        <div className="overflow-hidden rounded-lg border border-primary/25 bg-surface shadow-card">
          <div
            className={CABECALHO_TABELA}
            style={{ gridTemplateColumns: GRADE }}
          >
            <div />
            <div>Nome</div>
            <div>Pacote</div>
            <div className="text-right">Grupos</div>
            <div className="text-right">Dispositivos</div>
            <div className="pl-4">Status</div>
            <div />
          </div>

          {carregando &&
            Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="grid items-center border-b px-3.5"
                style={{ gridTemplateColumns: GRADE, borderColor: 'var(--gps-soft)', minHeight: 'var(--gps-row-h)' }}
              >
                <div />
                <Skeleton className="h-3 w-40" />
                <Skeleton className="h-3 w-48" />
                <Skeleton className="ml-auto h-3 w-8" />
                <Skeleton className="ml-auto h-3 w-12" />
                <Skeleton className="ml-4 h-4 w-16" />
                <div />
              </div>
            ))}

          {!carregando && erro && (
            <div className="p-8 text-center text-[12.5px] text-danger">
              {erro}
              <button
                type="button"
                onClick={carregar}
                className="ml-2 font-semibold text-primary underline underline-offset-2"
              >
                tentar de novo
              </button>
            </div>
          )}

          {!carregando && !erro && linhas.length === 0 && (
            <div className="p-8 text-center text-[12.5px] text-muted-foreground">
              Nenhum aplicativo encontrado com esse filtro.
            </div>
          )}

          {!carregando &&
            !erro &&
            linhas.map((a, i) => (
              <div
                // Chave e o packageName puro, sem o indice: se o GROUP BY do
                // backend algum dia devolver pacote repetido, o React AVISA em vez
                // de o `-i` engolir o problema.
                key={a.packageName || `sem-pacote-${i}`}
                /* A LINHA ABRE O CADASTRO (10/09/2026, a pedido).

                   Esta era a unica tela de cadastro em que clicar na linha nao
                   fazia nada. Usuarios, grupos, politicas e APKs passam pelo
                   `PainelLista`, que tem `aoClicarLinha` ligado ao modal de
                   edicao; aplicativos monta a propria tabela a mao e ficou de
                   fora - o unico caminho para abrir era o menu de tres pontos.

                   Nao foi convertido para o PainelLista agora porque esta tela
                   tem duas colunas de contagem clicaveis que aquele componente
                   nao preve; a conversao e maior que a correcao e pode vir
                   depois. O comportamento, que e o que se ve, ja e o mesmo. */
                onClick={() => a.packageName && setEditando(a.packageName)}
                className="group/linha grid cursor-pointer items-center border-b px-3.5 hover:bg-surface-2"
                style={{ gridTemplateColumns: GRADE, borderColor: 'var(--gps-soft)', minHeight: 'var(--gps-row-h)' }}
              >
                <div />
                <div className="flex min-w-0 items-center gap-2">
                  {/* O ICONE DO APP (10/09/2026, a pedido).

                      Vem do Google, por pacote, com cache por sessao - ver
                      IconeAplicativo. Numa lista de 795 aplicativos com nomes
                      parecidos ("Acesso", "Acess run", "Acesso (Lago Center)")
                      a arte e o que se reconhece antes de ler.

                      Quando nao ha icone nao entra nada no lugar: quadradinho
                      cinza repetido em metade das linhas vira ruido com a mesma
                      forma do dado. */}
                  <IconeAplicativo pacote={a.packageName} nome={a.name} />
                  {/* O nome ganha sublinhado no hover da linha. A linha inteira e
                      clicavel, mas o alvo que a pessoa MIRA e o nome - sem sinal
                      nenhum nele, o clique parece um acidente que deu certo. */}
                  <span className="block truncate text-[12.5px] font-semibold group-hover/linha:underline">
                    {a.name || ' - '}
                  </span>
                </div>
                <div className="block truncate text-[12px] text-muted-foreground">{a.packageName}</div>
                {/* Contagens clicaveis: abrem a lista de quais grupos e quais
                    dispositivos. O numero sozinho diz "14" e nao diz "quais",
                    que e a pergunta seguinte em toda vez que alguem olha. */}
                <button
                  type="button"
                  /* `stopPropagation` obrigatorio: sem ele o clique sobe para a
                     linha e abre o cadastro, engolindo o destino do numero.
                     O MenuAcoes ja faz isso por conta propria. */
                  onClick={(e) => {
                    e.stopPropagation();
                    setDetalhe({ app: a, modo: 'grupos' });
                  }}
                  className="text-right text-[12px] text-muted-foreground hover:text-primary hover:underline"
                  title="Ver os grupos deste aplicativo"
                >
                  <Num>{numero(a.gruposCount).toLocaleString('pt-BR')}</Num>
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDetalhe({ app: a, modo: 'dispositivos' });
                  }}
                  className="text-right text-[12px] font-medium hover:text-primary hover:underline"
                  title="Ver os dispositivos deste aplicativo"
                >
                  <Num>{numero(a.dispositivosCount).toLocaleString('pt-BR')}</Num>
                </button>
                <div className="pl-4">
                  <PillStatus ativo={ehAtivo(a)} />
                </div>
                <div className="flex justify-end">
                  <MenuAcoes<Aplicativo>
                    linha={a}
                    aberto={menuAberto === a.packageName}
                    onAlternar={() => setMenuAberto(menuAberto === a.packageName ? null : a.packageName ?? null)}
                    onFechar={() => setMenuAberto(null)}
                    ancorarADireita
                    acoes={[
                      {
                        rotulo: 'Abrir / editar',
                        ao: (x) => x.packageName && setEditando(x.packageName),
                      },
                      { rotulo: 'Ver grupos', ao: (x) => setDetalhe({ app: x, modo: 'grupos' }) },
                      { rotulo: 'Ver dispositivos', ao: (x) => setDetalhe({ app: x, modo: 'dispositivos' }) },
                      { rotulo: 'Limpar dados do app', ao: (x) => setCacheDe(x) },
                      {
                        rotulo: 'Apagar',
                        perigo: true,
                        /* SO MASTER (10/09/2026, a pedido).

                           Estava em `podeGravarCadastroAdmin`, que inclui Administrador - o
                           mesmo teste usado para CRIAR e EDITAR. Apagar nao pertence a essa
                           categoria: criar errado se corrige editando, apagar nao tem desfazer
                           e leva junto os vinculos do registro.

                           A autoridade continua sendo o backend; isto so evita mostrar uma
                           opcao que a pessoa nao deveria acionar. */
                        /* ESCONDIDO (29/09/2026): A ROTA NAO EXISTE.

                           O botao chamava DELETE /aplicativos/:packageName, e
                           aplicativos.controller.ts nao tem @Delete - importa o
                           decorador e nunca o usa. O modal prometia "o pacote sai
                           do catalogo e deixa de ser distribuido aos grupos", e a
                           resposta era 404. Nada acontecia.

                           POR QUE NAO CRIEI A ROTA: medido no banco, 786
                           aplicativos estao em politicas, com 147.330 vinculos.
                           Apagar de verdade exige decidir o que fazer com esses
                           vinculos E reenviar ao Google cada politica afetada - e
                           decisao de produto, nao correcao.

                           Para tirar um aplicativo de uso hoje, o caminho que
                           funciona e INATIVAR (status no cadastro). O modal de
                           confirmacao ficou no arquivo: implementada a rota, basta
                           voltar esta linha para `isMaster(user)`. */
                        visivel: false,
                        ao: (x) => setApagando(x),
                      },
                    ]}
                  />
                </div>
              </div>
            ))}

          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line px-3.5 py-2.5 pr-16 text-[12px] text-muted-foreground">
            <span>
              Mostrando <Num>{linhas.length}</Num> de <Num>{total.toLocaleString('pt-BR')}</Num> aplicativos
            </span>

            {/* SELETOR DE LINHAS no rodape, colado no contador.

                E ali que a pessoa percebe que a lista esta curta ("Mostrando 5
                de 30.229"), entao e ali que a correcao tem que estar - no topo,
                junto dos filtros, ele responderia a uma pergunta que ainda nao
                foi feita. */}
            <label className="flex items-center gap-1.5">
              por página
              <select
                className="h-7 rounded-md border border-line bg-surface px-1.5 text-[11px]"
                value={linhasPorPagina}
                onChange={(e) => {
                  setLinhasPorPagina(Number(e.target.value));
                  setPagina(0);
                }}
              >
                {OPCOES_LINHAS.map((n) => (
                  <option key={n} value={n}>
                    {rotuloLinhas(n)}
                  </option>
                ))}
              </select>
            </label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={pagina === 0}
                onClick={() => setPagina((p) => Math.max(0, p - 1))}
                className="h-7 rounded-md border border-line px-2 font-semibold disabled:opacity-40"
              >
                Anterior
              </button>
              <span>
                <Num>{pagina + 1}</Num> / <Num>{totalPaginas}</Num>
              </span>
              <button
                type="button"
                disabled={pagina + 1 >= totalPaginas}
                onClick={() => setPagina((p) => p + 1)}
                className="h-7 rounded-md border border-line px-2 font-semibold disabled:opacity-40"
              >
                Próxima
              </button>
            </div>
          </div>
        </div>
      <ModalNovoAplicativo aberto={modalAberto} onFechar={() => setModalAberto(false)} onCriado={carregar} />

      <ModalAplicativo
        aberto={editando !== undefined}
        packageName={editando}
        onFechar={() => setEditando(undefined)}
        onSalvo={carregar}
      />

      <Modal
        aberto={Boolean(apagando)}
        titulo="Apagar aplicativo?"
        descricao="O pacote sai do catálogo e deixa de ser distribuído aos grupos vinculados."
        icone="!"
        largura="max-w-[480px]"
        onFechar={() => setApagando(null)}
        rodape={
          <>
            <BotaoSecundario onClick={() => setApagando(null)} disabled={removendo}>
              Cancelar
            </BotaoSecundario>
            <button
              type="button"
              onClick={apagar}
              disabled={removendo}
              className="h-9 rounded-lg bg-danger px-4 text-[12.5px] font-bold text-white disabled:opacity-40"
            >
              {removendo ? 'Apagando...' : 'Apagar aplicativo'}
            </button>
          </>
        }
      >
        {apagando && (
          <ResumoExclusao
            titulo={apagando.name || '—'}
            detalhe={apagando.packageName}
            extra={
              <>
                Está em <strong className="text-ink">{numero(apagando.gruposCount)}</strong> grupo(s) e{' '}
                <strong className="text-ink">{numero(apagando.dispositivosCount)}</strong> dispositivo(s).
              </>
            }
          />
        )}
      </Modal>

      {/* Modais reaproveitados da tela antiga. */}
      <ModalLimparCache
        aberto={Boolean(cacheDe?.packageName)}
        aplicativo={cacheDe}
        onFechar={() => setCacheDe(null)}
        onExecutado={carregar}
      />

      {detalhe?.app?.packageName && (
        <ModalDetalheApp
          aberto={Boolean(detalhe)}
          onFechar={() => setDetalhe(null)}
          modo={detalhe.modo}
          packageName={detalhe.app.packageName}
          appName={detalhe.app.name}
        />
      )}
      </div>
    </Page>
  );
}
